import React, { useEffect, useState } from "react";
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { getSearchAnalytics } from "../../api/context/analytics";
import { FiBarChart2, FiRefreshCw, FiSearch, FiUsers, FiCalendar, FiInfo } from "react-icons/fi";
import Loader from '../../components/loader/loader';
import toast from "react-hot-toast";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";

const COLORS = [
    "#6366F1", "#22C55E", "#FACC15", "#F97316",
    "#EC4899", "#06B6D4", "#8B5CF6", "#F43F5E",
];

const SearchAnalytics = () => {

    const navigate = useNavigate()
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const getDefaultDates = () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 7);

        return {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
        };
    };

    const [filters, setFilters] = useState(getDefaultDates());

    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const fetchAnalytics = async (isRefresh = false) => {
        if (isRefresh) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }

        // Prepare filter parameters for REQUEST BODY
        const requestBody = {
            limit: 15,
            startDate: filters.startDate,
            endDate: filters.endDate
        };

        console.log("📅 Analytics params:", requestBody);

        try {
            // Wait for the API call to complete before setting loading to false
            await new Promise((resolve, reject) => {
                getSearchAnalytics(requestBody)((response) => {
                    if (response?.statusCode === 200) {
                        setAnalytics(response?.data);

                        // Calculate number of days and show toast
                        const start = new Date(filters.startDate);
                        const end = new Date(filters.endDate);
                        const diffTime = Math.abs(end - start);
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

                        toast.success(`Data fetched successfully for ${diffDays} ${diffDays === 1 ? 'day' : 'days'}`);

                        resolve(response);
                    } else {
                        setAnalytics(null);
                        reject(new Error('Failed to fetch analytics'));
                    }
                });
            });
        } catch (error) {
            console.error("Error fetching analytics:", error);
            setAnalytics(null);
            toast.error('Failed to fetch analytics data');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const handleFilterChange = (key, value) => {
        const today = getTodayDate();

        if (key === 'startDate') {
            // Check if start date is in future
            if (value > today) {
                toast.error('Start date cannot be in the future');
                return;
            }

            // Check if start date is after end date
            if (filters.endDate && value > filters.endDate) {
                toast.error('Start date cannot be after end date');
                return;
            }
        }

        if (key === 'endDate') {
            // Check if end date is in future
            if (value > today) {
                toast.error('End date cannot be in the future');
                return;
            }

            // Check if end date is before start date
            if (filters.startDate && value < filters.startDate) {
                toast.error('End date cannot be before start date');
                return;
            }
        }

        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    // Add useEffect to handle filter changes
    useEffect(() => {
        // Only fetch when we have both dates and it's not the initial default state
        const hasBothDates = filters.startDate && filters.endDate;
        const isDefaultState = JSON.stringify(filters) === JSON.stringify(getDefaultDates());

        if (hasBothDates && !isDefaultState) {
            const timer = setTimeout(() => {
                fetchAnalytics(true);
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [filters]);

    const handleClearFilters = () => {
        const defaultDates = getDefaultDates();
        setFilters(defaultDates);

        // Create request body with default dates directly
        const requestBody = {
            limit: 15,
            startDate: defaultDates.startDate,
            endDate: defaultDates.endDate
        };

        // Manually trigger fetch with default dates
        setTimeout(() => {
            setRefreshing(true);
            getSearchAnalytics(requestBody)((response) => {
                if (response?.statusCode === 200) {
                    setAnalytics(response?.data);
                } else {
                    setAnalytics(null);
                    toast.error('Failed to fetch analytics data');
                }
                setRefreshing(false);
            });
        }, 100);
    };

    const isFilterActive = () => {
        const defaultDates = getDefaultDates();
        return filters.startDate !== defaultDates.startDate || filters.endDate !== defaultDates.endDate;
    };

    const isLoadingInitial = loading && !refreshing;
    const hasNoData = !loading && !analytics;

    return (
        <div className="min-vh-100 search-analytics-page">
            <style>{`
                .search-analytics-page h3 { font-size: 18px !important; }
                .search-analytics-page h4 { font-size: 18px !important; }
                .search-analytics-page h5 { font-size: 14px !important; }
                .search-analytics-page h6 { font-size: 12px !important; }
                .search-analytics-page p { font-size: 12px; }
                .search-analytics-page .small,
                .search-analytics-page small { font-size: 11px !important; }
                .search-analytics-page .badge { font-size: 10px !important; padding: 3px 7px !important; }
                .search-analytics-page .card-body { padding: 12px !important; }
                .search-analytics-page .btn { font-size: 12px; }
                .search-analytics-page .recharts-text { font-size: 10px !important; }
                .search-analytics-page .table { font-size: 12px; }
                .search-analytics-page .table th { font-size: 11px !important; font-weight: 600; padding: 8px !important; }
                .search-analytics-page .table td { padding: 8px !important; }
                /* Uniform vertical spacing (16px) */
                .search-analytics-page .row { margin-bottom: 16px !important; margin-top: 0 !important; }
                .search-analytics-page .mb-2,
                .search-analytics-page .mb-3,
                .search-analytics-page .mb-4,
                .search-analytics-page .my-2 { margin-bottom: 16px !important; margin-top: 0 !important; }
                .search-analytics-page .card { margin-bottom: 0 !important; }

                /* Modern filter row (matches homepage) */
                .search-analytics-page .filters-row {
                    display: flex !important;
                    flex-wrap: nowrap !important;
                    align-items: center;
                    gap: 8px;
                    overflow-x: auto;
                    -webkit-overflow-scrolling: touch;
                    scrollbar-width: none;
                    padding: 4px 2px 6px;
                }
                .search-analytics-page .filters-row::-webkit-scrollbar { display: none; }
                .search-analytics-page .filters-row > * { flex-shrink: 0 !important; }
                .search-analytics-page .filters-to { white-space: nowrap; color: #94a3b8; padding: 0 2px; font-size: 12px; }
                @media (min-width: 768px) { .search-analytics-page .filters-row { gap: 12px; } }

                .search-analytics-page .modern-date {
                    display: inline-block !important;
                    border: 1px solid #e2e8f0 !important;
                    border-radius: 8px !important;
                    padding: 5px 8px !important;
                    width: 135px !important;
                    height: 34px !important;
                    background-color: #fff !important;
                    color: #0f172a !important;
                    margin: 0 !important;
                    font-family: inherit;
                    font-size: 12px !important;
                }
                .search-analytics-page .modern-date:focus {
                    border-color: #008695 !important;
                    box-shadow: 0 0 0 3px rgba(0,134,149,0.15) !important;
                    outline: none;
                }
                @media (max-width: 576px) {
                    .search-analytics-page .modern-date { width: 120px !important; padding: 5px 6px !important; }
                }
                .search-analytics-page .modern-refresh {
                    border: 1px solid #e2e8f0 !important;
                    background: #fff !important;
                    color: #475569 !important;
                    border-radius: 8px !important;
                    padding: 6px 12px !important;
                    font-weight: 500 !important;
                    height: 34px !important;
                    white-space: nowrap;
                    font-size: 12px !important;
                    transition: all 0.15s ease;
                }
                .search-analytics-page .modern-refresh:hover {
                    border-color: #008695 !important;
                    color: #008695 !important;
                    background: rgba(0,134,149,0.06) !important;
                }
                .search-analytics-page .filters-clear {
                    height: 34px !important;
                    border-radius: 8px !important;
                    white-space: nowrap;
                    font-size: 12px !important;
                }

                /* Mobile: header wrap, horizontally scroll filter row */
                @media (max-width: 767.98px) {
                    .search-analytics-page .page-header-sticky { flex-wrap: wrap !important; gap: 10px; }
                    .search-analytics-page .page-header-sticky > * { width: 100%; }
                    .search-analytics-page .overflow-auto::-webkit-scrollbar { display: none; }
                }
            `}</style>

            {/* Header — uses shared .page-header-sticky (same as homepage/analytics) */}
            <div className="page-header-sticky d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                    <div
                        className="back-btn"
                        onClick={() => navigate(-1)}
                        style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '30px',
                            height: '30px',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            background: '#fff',
                            transition: 'all 0.15s ease',
                        }}
                    >
                        <IoMdArrowBack style={{ fontSize: '16px', color: '#475569' }} />
                    </div>
                    <h4 style={{ fontSize: '18px', fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.01em' }}>Search Analytics</h4>
                </div>

                <div className="filters-row">
                    <input
                        type="date"
                        className="modern-date"
                        value={filters.startDate}
                        onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        max={filters.endDate || getTodayDate()}
                    />
                    <span className="filters-to">to</span>
                    <input
                        type="date"
                        className="modern-date"
                        value={filters.endDate}
                        onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        min={filters.startDate || undefined}
                        max={getTodayDate()}
                    />
                    {isFilterActive() && (
                        <button
                            onClick={handleClearFilters}
                            className="btn btn-sm btn-outline-secondary filters-clear"
                            disabled={refreshing}
                        >
                            Clear
                        </button>
                    )}
                    <button
                        onClick={() => fetchAnalytics(true)}
                        className="btn modern-refresh d-flex align-items-center gap-2"
                        disabled={refreshing}
                    >
                        <FiRefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
                        <span>{refreshing ? 'Refreshing' : 'Refresh'}</span>
                    </button>
                </div>
            </div>

            {isLoadingInitial ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 280 }}>
                    <Loader />
                </div>
            ) : hasNoData ? (
                <div className="d-flex flex-column align-items-center justify-content-center text-center text-muted" style={{ minHeight: 280 }}>
                    <FiInfo size={32} className="mb-3 text-secondary" />
                    <h5 className="fw-bold mb-2">No Data Available</h5>
                    <p className="mb-0" style={{ maxWidth: 420 }}>
                        We couldn't find any analytics to display right now. Try refreshing the page or check back later.
                    </p>
                </div>
            ) : (
              <>
            {/* Summary Cards */}
            <div className="row g-3">
                {[
                    {
                        title: "Total Search Terms",
                        value: analytics.summary.totalSearchTerms,
                        icon: <FiSearch />,
                        accent: '#008695',
                    },
                    {
                        title: "Total Searches",
                        value: analytics.engagementMetrics.totalSearches,
                        icon: <FiBarChart2 />,
                        accent: '#6366F1',
                    },
                    {
                        title: "Active Users",
                        value: analytics.engagementMetrics.activeUsers,
                        icon: <FiUsers />,
                        accent: '#22C55E',
                    },
                ].map((card, i) => (
                    <div key={i} className="col-md-4">
                        <div style={{
                            background: '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '10px',
                            padding: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                        }}>
                            <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '10px',
                                background: `${card.accent}15`,
                                color: card.accent,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}>
                                {React.cloneElement(card.icon, { size: 16 })}
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ fontSize: '11px', fontWeight: 500, color: '#64748b', marginBottom: '2px' }}>{card.title}</div>
                                <div style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', lineHeight: 1.1 }}>{card.value}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Charts Grid */}
            <div>
                {/* Popular Searches */}
                <div>
                    <h5 className="fw-bold" style={{ margin: '0 0 10px' }}>Popular Searches</h5>
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                        <div className="row g-3">
                            {/* Left Column - Chart */}
                            <div className="col-md-8" style={{ background: '#f8fafc', borderRadius: '8px' }}>
                                    <div style={{ width: "100%", height: 220, padding: 0, marginTop: 20, marginBottom: 20, paddingRight: 20 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                padding={0}
                                                data={analytics.popularSearches}
                                                margin={{ top: 0, right: 0, left: 0, bottom: 20 }}
                                                barCategoryGap="50%"
                                                barGap={20}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis
                                                    dataKey="searchTerm"
                                                    angle={-40}
                                                    textAnchor="end"
                                                    interval={0}
                                                    tick={{ fontSize: 10, fontWeight: 600 }}
                                                    padding={{ left: 0, right: 0 }}
                                                />
                                                <YAxis tick={{ fontSize: 10, fontWeight: 600 }} />
                                                <Tooltip cursor={{ fill: "transparent" }} />
                                                <Bar
                                                    dataKey="totalSearches"
                                                    fill="#15b8b8ff"
                                                    radius={[6, 6, 0, 0]}
                                                    barSize={40}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                                {/* Right Column - Scrollable List */}
                                <div className="col-md-4">
                                    <div
                                        style={{
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '8px',
                                            maxHeight: 260,
                                            overflowY: 'auto',
                                            backgroundColor: '#f8fafc',
                                            padding: '10px',
                                        }}
                                    >
                                        <h6 style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textAlign: 'center', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Top Search Terms</h6>
                                        <div className="d-flex flex-column gap-1">
                                            {analytics.popularSearches.map((search, index) => (
                                                <div
                                                    key={index}
                                                    className="d-flex justify-content-between align-items-center"
                                                    style={{
                                                        backgroundColor: 'white',
                                                        border: '1px solid #eef2f7',
                                                        borderRadius: '6px',
                                                        padding: '6px 8px',
                                                    }}
                                                >
                                                    <div className="d-flex align-items-center" style={{ gap: '6px', minWidth: 0 }}>
                                                        <span style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', minWidth: '18px' }}>
                                                            #{index + 1}
                                                        </span>
                                                        <span
                                                            className="text-truncate"
                                                            style={{ fontSize: '11px', fontWeight: 500, color: '#0f172a', maxWidth: '110px' }}
                                                            title={search.searchTerm}
                                                        >
                                                            {search.searchTerm}
                                                        </span>
                                                    </div>
                                                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#008695' }}>
                                                        {search.totalSearches}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                </div>

                {/* Search Trends */}
                <div>
                    <h5 className="fw-bold" style={{ margin: '0 0 10px' }}>Search Trends</h5>
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                            <div style={{ width: "100%", height: 240, background: '#f8fafc', borderRadius: '8px', padding: '10px 0' }}>
                                <ResponsiveContainer>
                                    <LineChart data={analytics.searchTrends} margin={{ top: 50, bottom: 50, right: 30 }}>
                                        <CartesianGrid strokeDasharray="3 3" />

                                        {/* X & Y axis with smaller font */}
                                        <XAxis
                                            dataKey="date"
                                            angle={-40}
                                            textAnchor="end"
                                            tick={{ fontSize: 10, fontWeight: 600 }}
                                        />
                                        <YAxis tick={{ fontSize: 10, fontWeight: 600 }} />

                                        {/* Customized tooltip */}
                                        <Tooltip
                                            content={({ active, payload, label }) => {
                                                if (active && payload && payload.length) {
                                                    return (
                                                        <div
                                                            className="p-2 rounded-2 shadow-sm"
                                                            style={{
                                                                background: "#fff",
                                                                border: "1px solid #ddd",
                                                                fontSize: "12px",
                                                                lineHeight: "1.4"
                                                            }}
                                                        >
                                                            <p className="fw-bold mb-1">{label}</p>
                                                            {payload.map((item, index) => (
                                                                <p
                                                                    key={index}
                                                                    style={{ color: item.stroke, margin: 0 }}
                                                                >
                                                                    {item.name}: <strong>{item.value}</strong>
                                                                </p>
                                                            ))}
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />

                                        {/* Lines */}
                                        <Line
                                            type="monotone"
                                            dataKey="dailySearches"
                                            name="Daily Searches"
                                            stroke="#06aebaff"
                                            strokeWidth={2}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="uniqueUsers"
                                            name="Unique Users"
                                            stroke="#ff0000ff"
                                            strokeWidth={2}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                </div>

                {/* Zero Result Searches */}
                <div>
                    <h5 className="fw-bold" style={{ margin: '0 0 10px' }}>Zero Result Searches</h5>
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                            {analytics.zeroResultSearches && analytics.zeroResultSearches.length > 0 ? (
                                <div
                                    className="d-flex overflow-auto pb-2 hide-scrollbar"
                                    style={{
                                        whiteSpace: "nowrap",
                                        gap: "1rem",
                                        padding: 0,
                                        margin: 0,
                                    }}
                                >
                                    {analytics.zeroResultSearches.map((item, idx) => (
                                        <div
                                            key={idx}
                                            className="d-inline-flex flex-column justify-content-center align-items-center"
                                            style={{
                                                minWidth: "120px",
                                                background: "#f8fafc",
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                padding: '10px 12px',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <span style={{ fontSize: "16px", fontWeight: 700, color: '#0f172a', marginBottom: "2px" }}>
                                                {item.totalAttempts}
                                            </span>
                                            <span
                                                className="text-center text-truncate"
                                                style={{
                                                    maxWidth: "100px",
                                                    fontSize: "11px",
                                                    color: "#64748b",
                                                    fontWeight: 500,
                                                }}
                                                title={item.searchTerm}
                                            >
                                                {item.searchTerm}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-muted" style={{ margin: 0 }}>No zero-result searches found.</p>
                            )}
                    </div>
                </div>

                {/* User Search Patterns */}
                <div>
                    <h5 className="fw-bold" style={{ margin: '0 0 10px' }}>User Search Patterns</h5>
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', marginBottom: '16px', overflow: 'auto' }}>
                            <div className="table-responsive">
                                <table className="table table-bordered table-hover text-center align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            {"User ID,Total Searches,Total Actions,Avg Relevance,Last Search Date".split(",").map((header, idx) => (
                                                <th key={idx}>{header}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {analytics.userSearchPatterns.map((user, idx) => (
                                            <tr key={idx}>
                                                <td>{user.userId}</td>
                                                <td>{user.totalSearches}</td>
                                                <td>{user.totalSearchActions}</td>
                                                <td>{user.avgRelevance || "N/A"}</td>
                                                <td>{new Date(user.lastSearchDate).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                    </div>
                </div>
            </div>
              </>
            )}

            {/* CSS Styles */}
            <style>
                {`
                .hide-scrollbar::-webkit-scrollbar {
                    display: none; /* Chrome, Safari */
                }
                .hide-scrollbar {
                    -ms-overflow-style: none;  /* IE and Edge */
                    scrollbar-width: none;     /* Firefox */
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                `}
            </style>
        </div>
    );
};

export default SearchAnalytics;